/**
 * @file PhotoCapture.jsx
 * @description Camera / file picker component for capturing inspection photos.
 *
 * @param {Array<{id:string, src:string, file?:File}>} [photos=[]] - Current photo list
 * @param {Function} [onChange] - Called with updated photos array
 * @param {number} [maxPhotos=20] - Maximum number of photos
 * @param {number} [maxFileSize=10] - Max file size in MB
 * @param {number} [compressionQuality=0.85] - JPEG compression quality (0-1)
 * @param {boolean} [disabled=false]
 * @param {boolean} [sortable=true] - Allow drag to reorder
 * @param {string} [className]
 */
import React, { useState, useRef, useCallback, useId } from 'react';

/** Camera icon */
function CameraIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

/** Plus icon */
function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

/** X icon */
function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/** Left arrow for lightbox */
function ArrowLeft() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/** Right arrow for lightbox */
function ArrowRight() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

/**
 * Compress an image file client-side.
 * @param {File} file
 * @param {number} quality - 0 to 1
 * @param {number} maxDimension - max width/height in px
 * @returns {Promise<{blob: Blob, dataUrl: string}>}
 */
function compressImage(file, quality = 0.85, maxDimension = 2048) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      // Scale down if needed
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Compression failed'));
          const dataUrl = URL.createObjectURL(blob);
          resolve({ blob, dataUrl });
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

let idCounter = 0;
function generateId() {
  return `photo-${Date.now()}-${++idCounter}`;
}

function PhotoCapture({
  photos = [],
  onChange,
  maxPhotos = 20,
  maxFileSize = 10,
  compressionQuality = 0.85,
  disabled = false,
  sortable = true,
  className = '',
  style: userStyle,
  ...rest
}) {
  const inputRef = useRef(null);
  const instanceId = useId();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [errors, setErrors] = useState([]);

  const canAddMore = photos.length < maxPhotos;

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const newErrors = [];
    const newPhotos = [];

    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        newErrors.push(`${file.name}: Not an image file`);
        continue;
      }

      // Validate file size
      if (file.size > maxFileSize * 1024 * 1024) {
        newErrors.push(`${file.name}: Exceeds ${maxFileSize}MB limit`);
        continue;
      }

      // Check total limit
      if (photos.length + newPhotos.length >= maxPhotos) {
        newErrors.push(`Maximum ${maxPhotos} photos reached`);
        break;
      }

      try {
        const { blob, dataUrl } = await compressImage(file, compressionQuality);
        newPhotos.push({
          id: generateId(),
          src: dataUrl,
          file: new File([blob], file.name, { type: 'image/jpeg' }),
          name: file.name,
        });
      } catch {
        newErrors.push(`${file.name}: Failed to process`);
      }
    }

    setErrors(newErrors);
    if (newPhotos.length > 0 && onChange) {
      onChange([...photos, ...newPhotos]);
    }

    // Reset input so same file can be selected again
    if (inputRef.current) inputRef.current.value = '';
  }, [photos, onChange, maxPhotos, maxFileSize, compressionQuality]);

  const handleDelete = useCallback((index) => {
    if (onChange) {
      const updated = photos.filter((_, i) => i !== index);
      onChange(updated);
    }
    setDeleteConfirm(null);
    setLightboxIndex(null);
  }, [photos, onChange]);

  // Drag and drop reorder
  const handleDragStart = useCallback((index) => {
    if (!sortable) return;
    setDragIndex(index);
  }, [sortable]);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((index) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const updated = [...photos];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(index, 0, moved);
    if (onChange) onChange(updated);
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, photos, onChange]);

  // Lightbox navigation
  const lightboxNav = useCallback((dir) => {
    setLightboxIndex(prev => {
      if (prev === null) return null;
      const next = prev + dir;
      if (next < 0 || next >= photos.length) return prev;
      return next;
    });
  }, [photos.length]);

  // Lightbox keyboard
  React.useEffect(() => {
    if (lightboxIndex === null) return;
    function handleKey(e) {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') lightboxNav(-1);
      if (e.key === 'ArrowRight') lightboxNav(1);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, lightboxNav]);

  const thumbnailSize = '80px';

  return (
    <div className={`ib-photo-capture ${className}`} style={userStyle} {...rest}>
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        id={`${instanceId}-input`}
        disabled={disabled || !canAddMore}
      />

      {/* Thumbnail grid */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--space-2)',
        alignItems: 'flex-start',
      }}>
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            draggable={sortable && !disabled}
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={() => handleDrop(index)}
            onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
            onClick={() => setLightboxIndex(index)}
            style={{
              position: 'relative',
              width: thumbnailSize,
              height: thumbnailSize,
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              cursor: 'pointer',
              border: dragOverIndex === index ? '2px solid var(--color-primary)' : '2px solid var(--border-default)',
              opacity: dragIndex === index ? 0.4 : 1,
              transition: 'all var(--transition-fast)',
              flexShrink: 0,
            }}
          >
            <img
              src={photo.src}
              alt={photo.name || `Photo ${index + 1}`}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {/* Delete button */}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm(index);
                }}
                aria-label={`Delete photo ${index + 1}`}
                style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  width: '22px',
                  height: '22px',
                  borderRadius: 'var(--radius-full)',
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              >
                <XIcon />
              </button>
            )}
          </div>
        ))}

        {/* Add Photo button */}
        {canAddMore && !disabled && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            aria-label="Add photo"
            style={{
              width: thumbnailSize,
              height: thumbnailSize,
              borderRadius: 'var(--radius-md)',
              border: '2px dashed var(--border-default)',
              backgroundColor: 'var(--surface-sunken)',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-1)',
              fontSize: 'var(--text-caption)',
              fontFamily: 'var(--font-family)',
              transition: 'all var(--transition-fast)',
              flexShrink: 0,
            }}
          >
            {photos.length === 0 ? <CameraIcon /> : <PlusIcon />}
            <span>{photos.length === 0 ? 'Photo' : 'Add'}</span>
          </button>
        )}
      </div>

      {/* Photo count */}
      {photos.length > 0 && (
        <div style={{
          marginTop: 'var(--space-2)',
          fontSize: 'var(--text-caption)',
          color: 'var(--text-secondary)',
        }}>
          {photos.length} / {maxPhotos} photos
        </div>
      )}

      {/* Error messages */}
      {errors.length > 0 && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          {errors.map((err, i) => (
            <div key={i} style={{ fontSize: 'var(--text-caption)', color: 'var(--text-error)', marginBottom: 'var(--space-1)' }}>
              {err}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm !== null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'var(--overlay-medium)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 'var(--z-modal)',
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--surface-raised)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-5)',
              maxWidth: '300px',
              width: '90%',
              textAlign: 'center',
            }}
          >
            <p style={{ marginBottom: 'var(--space-4)', fontWeight: 'var(--weight-medium)' }}>Delete this photo?</p>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                style={{
                  flex: 1,
                  padding: 'var(--space-2_5) var(--space-4)',
                  border: '2px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--surface-raised)',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-family)',
                  fontSize: 'var(--text-body-sm)',
                  fontWeight: 'var(--weight-semibold)',
                  cursor: 'pointer',
                  minHeight: 'var(--touch-min)',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm)}
                style={{
                  flex: 1,
                  padding: 'var(--space-2_5) var(--space-4)',
                  border: '2px solid var(--color-error)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-error)',
                  color: '#fff',
                  fontFamily: 'var(--font-family)',
                  fontSize: 'var(--text-body-sm)',
                  fontWeight: 'var(--weight-semibold)',
                  cursor: 'pointer',
                  minHeight: 'var(--touch-min)',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && photos[lightboxIndex] && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 'var(--z-modal)',
          }}
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => setLightboxIndex(null)}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 'var(--space-4)',
              right: 'var(--space-4)',
              width: '44px',
              height: '44px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <XIcon />
          </button>

          {/* Navigation */}
          {lightboxIndex > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); lightboxNav(-1); }}
              aria-label="Previous photo"
              style={{
                position: 'absolute',
                left: 'var(--space-4)',
                width: '48px',
                height: '48px',
                borderRadius: 'var(--radius-full)',
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ArrowLeft />
            </button>
          )}

          {lightboxIndex < photos.length - 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); lightboxNav(1); }}
              aria-label="Next photo"
              style={{
                position: 'absolute',
                right: 'var(--space-4)',
                width: '48px',
                height: '48px',
                borderRadius: 'var(--radius-full)',
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ArrowRight />
            </button>
          )}

          {/* Image */}
          <img
            src={photos[lightboxIndex].src}
            alt={photos[lightboxIndex].name || `Photo ${lightboxIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 'var(--radius-md)',
            }}
          />

          {/* Counter */}
          <div style={{
            position: 'absolute',
            bottom: 'var(--space-4)',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255,255,255,0.7)',
            fontSize: 'var(--text-body-sm)',
          }}>
            {lightboxIndex + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotoCapture;
